import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, LogOut, Search, Loader2 } from "lucide-react";
import { api, authHeaders, formatApiError } from "@/lib/api";
import { Logo } from "@/components/site/Header";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LeadsTable } from "./LeadsTable";
import { TranscriptDialog } from "./TranscriptDialog";

const FILTERS = [
  { key: "category", label: "Category", opts: ["Residential", "Commercial", "Industrial", "PM Surya Ghar Yojana"] },
  { key: "status", label: "Lead", opts: ["complete", "partial"] },
  { key: "admin_status", label: "Status", opts: ["New", "Contacted", "Converted"] },
];

const Stat = ({ label, value, id }) => (
  <div className="glass rounded-2xl p-5" data-testid={`admin-stat-${id}`}>
    <div className="text-sm uppercase tracking-[0.18em] text-slate-500">{label}</div>
    <div className="mt-1 font-display text-3xl font-extrabold text-gold-ink">{value ?? "–"}</div>
  </div>
);

export const AdminDashboard = ({ onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [f, setF] = useState({ search: "", category: "all", status: "all", admin_status: "all" });
  const [viewId, setViewId] = useState(null);

  const params = useCallback(() => Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== "all")), [f]);

  const handleErr = useCallback((e) => {
    if (e?.response?.status === 401) { toast.error("Session expired, please login again"); onLogout(); }
    else toast.error(formatApiError(e));
  }, [onLogout]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/admin/leads", { params: params(), headers: authHeaders() }),
        api.get("/admin/stats", { headers: authHeaders() }),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (e) { handleErr(e); } finally { setLoading(false); }
  }, [params, handleErr]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const updateStatus = async (id, admin_status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { admin_status }, { headers: authHeaders() });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, admin_status } : l)));
      toast.success(`Status: ${admin_status}`);
      api.get("/admin/stats", { headers: authHeaders() }).then((s) => setStats(s.data));
    } catch (e) { handleErr(e); }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/export", { params: params(), headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `anuj-solar-leads-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { handleErr(e); } finally { setExporting(false); }
  };

  return (
    <div data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-cream-50/85 backdrop-blur-xl">
        <div className="container-x flex h-20 items-center justify-between">
          <Logo testId="admin-header-logo" />
          <button onClick={onLogout} data-testid="admin-logout-btn" className="btn-ghost h-11 px-5 text-[15px]"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
      </header>
      <main className="container-x py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-navy-900">Leads</h1>
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-5">
          <Stat id="total" label="Total" value={stats.total} />
          <Stat id="complete" label="Complete" value={stats.complete} />
          <Stat id="partial" label="Partial" value={stats.partial} />
          <Stat id="contacted" label="Contacted" value={stats.contacted} />
          <Stat id="converted" label="Converted" value={stats.converted} />
        </div>
        <div className="mt-8 flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
            <input value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} placeholder="Search name / mobile / address" data-testid="admin-leads-search-input" className="h-12 w-full rounded-full border border-slate-200 bg-white pl-12 pr-4 text-navy-900 outline-none focus:border-gold/60" />
          </div>
          <div className="grid grid-cols-3 gap-3 lg:flex">
            {FILTERS.map((fl) => (
              <Select key={fl.key} value={f[fl.key]} onValueChange={(v) => setF({ ...f, [fl.key]: v })}>
                <SelectTrigger data-testid={`admin-filter-${fl.key}`} className="h-12 rounded-full bg-white lg:w-48"><SelectValue placeholder={fl.label} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All {fl.label}</SelectItem>
                  {fl.opts.map((o) => <SelectItem key={o} value={o} data-testid={`admin-filter-${fl.key}-option-${o.replace(/\s+/g, "-").toLowerCase()}`}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            ))}
          </div>
          <button onClick={exportXlsx} disabled={exporting} data-testid="admin-leads-export-btn" className="btn-gold h-12 px-6 text-[15px] disabled:opacity-60">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Excel Download
          </button>
        </div>
        <LeadsTable leads={leads} loading={loading} onStatus={updateStatus} onView={setViewId} />
      </main>
      <TranscriptDialog leadId={viewId} onClose={() => setViewId(null)} onError={handleErr} />
    </div>
  );
};
